<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="customers"
      :search="search"
      :loading="loading"
      :sort-by="[{ key: 'submittedAt', order: 'desc' }]"
      show-expand
      v-model:expanded="expanded"
      item-value="id"
    >
      <template v-slot:loading>
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </template>

      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Clients</v-toolbar-title>

          <v-btn
            :disabled="loading"
            append-icon="mdi-refresh"
            text="Refresh"
            variant="outlined"
            @click="initialize"
          ></v-btn>

          <v-divider class="mx-4" inset vertical></v-divider>

          <v-text-field
            class="mr-5"
            v-model="search"
            label="Search a client"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            hide-details
            single-line
          ></v-text-field>

          <!-- Edit dialog: contact details only. The AWS Marketplace
               identifiers come from the subscription token and are shown
               read-only so the portal cannot desynchronise them. -->
          <v-dialog v-model="dialog" max-width="600px">
            <v-card>
              <v-card-title>
                <span class="text-h5">Client information</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.company"
                        label="Company"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.title"
                        label="Title"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.name"
                        label="Last name"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.forname"
                        label="First name"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.email"
                        label="Email"
                        type="email"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.phone"
                        label="Phone"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-divider class="mb-3"></v-divider>
                      <span class="text-subtitle-2">
                        AWS Marketplace information (read-only)
                      </span>
                      <v-table density="compact" class="mt-2">
                        <tbody>
                          <tr>
                            <td>Customer AWS Account ID</td>
                            <td>
                              {{
                                editedItem.customerAWSAccountId ||
                                "(not available)"
                              }}
                            </td>
                          </tr>
                          <tr>
                            <td>Customer Identifier</td>
                            <td>
                              {{
                                editedItem.customerIdentifier ||
                                "(not available)"
                              }}
                            </td>
                          </tr>
                          <tr>
                            <td>Product Code</td>
                            <td>
                              {{ editedItem.productCode || "(not available)" }}
                            </td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue-darken-1" variant="text" @click="close">
                  Cancel
                </v-btn>
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  @click="save"
                  :loading="editLoading"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:[`item.submittedAt`]="{ item }">
        {{ formatDate(item.submittedAt) }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip text="Edit client information">
          <template v-slot:activator="{ props }">
            <v-icon
              class="me-2"
              size="small"
              v-bind="props"
              @click="editItem(item)"
              >mdi-pencil</v-icon
            >
          </template>
        </v-tooltip>
      </template>

      <!-- Expanded row: AWS Marketplace identifiers -->
      <template v-slot:expanded-row="{ columns, item }">
        <tr>
          <td :colspan="columns.length">
            <v-container class="py-3">
              <span class="text-subtitle-2">AWS Marketplace information</span>
              <v-table density="compact" class="mt-2">
                <tbody>
                  <tr>
                    <td>Customer AWS Account ID</td>
                    <td>{{ item.customerAWSAccountId || "(not available)" }}</td>
                  </tr>
                  <tr>
                    <td>Customer Identifier</td>
                    <td>{{ item.customerIdentifier || "(not available)" }}</td>
                  </tr>
                  <tr>
                    <td>Product Code</td>
                    <td>{{ item.productCode || "(not available)" }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-container>
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        <p class="pa-4">No clients yet.</p>
      </template>
    </v-data-table>

    <v-snackbar v-model="snackbar">
      {{ alertText }}
      <template v-slot:actions>
        <v-btn color="pink" variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import CustomerService from "@/services/CustomerService";

const EMPTY_CUSTOMER = {
  id: "",
  company: "",
  name: "",
  forname: "",
  title: "",
  email: "",
  phone: "",
  submittedAt: "",
  customerAWSAccountId: "",
  customerIdentifier: "",
  productCode: "",
};

export default {
  data: () => ({
    search: "",
    loading: true,
    snackbar: false,
    alertText: "",
    expanded: [],
    dialog: false,
    editLoading: false,
    editedIndex: -1,
    editedItem: Object.assign({}, EMPTY_CUSTOMER),
    headers: [
      { title: "Company", key: "company" },
      { title: "Last name", key: "name" },
      { title: "First name", key: "forname" },
      { title: "Title", key: "title" },
      { title: "Email", key: "email" },
      { title: "Phone", key: "phone" },
      { title: "Submitted at", key: "submittedAt" },
      { title: "Actions", key: "actions", sortable: false },
      { title: "", key: "data-table-expand" },
    ],
    customers: [],
  }),

  watch: {
    dialog(val) {
      if (!val) this.close();
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      try {
        const response = await CustomerService.getCustomers();
        const list = Array.isArray(response) ? response : [];
        this.customers = list.map((c) => ({
          id: c.id,
          company: c.company || "-",
          name: c.name || "-",
          forname: c.forname || "-",
          title: c.title || "-",
          email: c.email || "-",
          phone: c.phone || "-",
          submittedAt: c.submittedAt || "",
          customerAWSAccountId: c.customerAWSAccountId || "",
          customerIdentifier: c.customerIdentifier || "",
          productCode: c.productCode || "",
        }));
      } catch (e) {
        this.alertText = "Error loading clients.";
        this.snackbar = true;
        this.customers = [];
      } finally {
        this.loading = false;
      }
    },

    editItem(item) {
      this.editedIndex = this.customers.indexOf(item);
      // Placeholder dashes are for display only; blank them out for editing.
      const copy = Object.assign({}, item);
      ["company", "name", "forname", "title", "email", "phone"].forEach((f) => {
        if (copy[f] === "-") copy[f] = "";
      });
      this.editedItem = copy;
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, EMPTY_CUSTOMER);
        this.editedIndex = -1;
      });
    },

    async save() {
      if (this.editedIndex < 0) return;

      this.editLoading = true;
      try {
        const response = await CustomerService.updateCustomer(this.editedItem);
        // Re-fetch so the table reflects exactly what was persisted.
        await this.initialize();
        this.alertText = (response && response.message) || "Customer updated.";
      } catch (e) {
        this.alertText = "Error updating the customer.";
      } finally {
        this.editLoading = false;
        this.snackbar = true;
        this.close();
      }
    },

    formatDate(iso) {
      if (!iso) return "-";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    },
  },
};
</script>
